// Report Service - API calls for report generation and management

import axiosInstance from '../../../lib/api/axiosInstance';
import { API_CONFIG, getAuthToken } from '../../../lib/config/api';
import type {
  ReportGenerationRequest,
  ReportGenerationResponse,
  GeneratedReport,
  ReportTemplate,
  TemplateListResponse,
  CreateTemplateRequest,
  GenerateWithTemplateRequest,
  PreviewRequest,
  PreviewResponse,
  CreateShareRequest,
  ShareResponse,
  ShareListResponse,
  ReportShare,
  DataSourcesResponse,
  DataSourceMap,
  TemplateType
} from '../types/report.types';

const API_BASE_URL = '/reports';
const TEMPLATES_BASE_URL = '/reports/templates';

class ReportService {
  // =====================================================================
  // Legacy Report Generation
  // =====================================================================

  /**
   * Generate a new report (legacy method)
   */
  async generateReport(request: ReportGenerationRequest): Promise<ReportGenerationResponse> {
    try {
      const response = await axiosInstance.post<ReportGenerationResponse>(
        `${API_BASE_URL}/generate`,
        request
      );

      return response.data;
    } catch (error: any) {
      console.error('Failed to generate report:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to generate report'
      };
    }
  }

  /**
   * Get all reports for a hotel
   */
  async getHotelReports(hotelId: string): Promise<GeneratedReport[]> {
    try {
      const response = await axiosInstance.get(`${API_BASE_URL}/hotel/${hotelId}`);

      return response.data.reports || [];
    } catch (error: any) {
      console.error('Failed to fetch hotel reports:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch reports');
    }
  }

  /**
   * Get all reports for the authenticated user
   */
  async getAllReports(): Promise<GeneratedReport[]> {
    try {
      const response = await axiosInstance.get(API_BASE_URL);

      return response.data.reports || [];
    } catch (error: any) {
      console.error('Failed to fetch reports:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch reports');
    }
  }

  /**
   * Download a report
   */
  async downloadReport(reportId: string, filename: string): Promise<void> {
    try {
      const response = await axiosInstance.get(`${API_BASE_URL}/${reportId}/download`, {
        responseType: 'blob'
      });

      // Create a download link and trigger it
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Failed to download report:', error);
      throw new Error(error.response?.data?.error || 'Failed to download report');
    }
  }

  /**
   * Delete a report
   */
  async deleteReport(reportId: string): Promise<void> {
    try {
      await axiosInstance.delete(`${API_BASE_URL}/${reportId}`);
    } catch (error: any) {
      console.error('Failed to delete report:', error);
      throw new Error(error.response?.data?.error || 'Failed to delete report');
    }
  }

  /**
   * Get download URL for a report (for preview/iframe)
   */
  getDownloadUrl(reportId: string): string {
    const token = getAuthToken();
    return `${API_CONFIG.BASE_URL}${API_BASE_URL}/${reportId}/download?token=${token}`;
  }

  // =====================================================================
  // Template Management
  // =====================================================================

  /**
   * Get all available templates (system + custom)
   * @param type - Filter by 'system', 'custom', or 'all'
   */
  async getTemplates(type: TemplateType | 'all' = 'all'): Promise<ReportTemplate[]> {
    try {
      const response = await axiosInstance.get<TemplateListResponse>(
        `${TEMPLATES_BASE_URL}`,
        { params: { type } }
      );

      return response.data.templates || [];
    } catch (error: any) {
      console.error('Failed to fetch templates:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch templates');
    }
  }

  /**
   * Get a single template by ID
   * @param templateId - System template name or custom template UUID
   */
  async getTemplate(templateId: string): Promise<ReportTemplate> {
    try {
      const response = await axiosInstance.get<{ success: boolean; template: ReportTemplate }>(
        `${TEMPLATES_BASE_URL}/${templateId}`
      );

      return response.data.template;
    } catch (error: any) {
      console.error('Failed to fetch template:', error);
      throw new Error(error.response?.data?.error || 'Template not found');
    }
  }

  /**
   * Create a custom template
   */
  async createCustomTemplate(data: CreateTemplateRequest): Promise<ReportTemplate> {
    try {
      const response = await axiosInstance.post<{ success: boolean; template: ReportTemplate }>(
        `${TEMPLATES_BASE_URL}/custom`,
        data
      );

      return response.data.template;
    } catch (error: any) {
      console.error('Failed to create template:', error);
      throw new Error(error.response?.data?.error || 'Failed to create template');
    }
  }

  /**
   * Update a custom template
   */
  async updateCustomTemplate(
    templateId: string,
    data: Partial<CreateTemplateRequest>
  ): Promise<ReportTemplate> {
    try {
      const response = await axiosInstance.put<{ success: boolean; template: ReportTemplate }>(
        `${TEMPLATES_BASE_URL}/${templateId}`,
        data
      );

      return response.data.template;
    } catch (error: any) {
      console.error('Failed to update template:', error);
      throw new Error(error.response?.data?.error || 'Failed to update template');
    }
  }

  /**
   * Delete a custom template
   */
  async deleteCustomTemplate(templateId: string): Promise<void> {
    try {
      await axiosInstance.delete(`${TEMPLATES_BASE_URL}/${templateId}`);
    } catch (error: any) {
      console.error('Failed to delete template:', error);
      throw new Error(error.response?.data?.error || 'Failed to delete template');
    }
  }

  // =====================================================================
  // Template-Based Report Generation
  // =====================================================================

  /**
   * Generate an HTML preview without saving
   */
  async previewReport(request: PreviewRequest): Promise<PreviewResponse> {
    try {
      const response = await axiosInstance.post<PreviewResponse>(
        `${API_BASE_URL}/preview`,
        request
      );

      return response.data;
    } catch (error: any) {
      console.error('Failed to generate preview:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to generate preview'
      };
    }
  }

  /**
   * Generate a report using a template
   */
  async generateWithTemplate(request: GenerateWithTemplateRequest): Promise<ReportGenerationResponse> {
    try {
      const response = await axiosInstance.post<ReportGenerationResponse>(
        `${API_BASE_URL}/generate-with-template`,
        request
      );

      return response.data;
    } catch (error: any) {
      console.error('Failed to generate report with template:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to generate report'
      };
    }
  }

  /**
   * Regenerate an existing report with latest data
   */
  async regenerateReport(reportId: string, outputFormat?: 'pdf' | 'html'): Promise<ReportGenerationResponse> {
    try {
      const response = await axiosInstance.post<ReportGenerationResponse>(
        `${API_BASE_URL}/${reportId}/regenerate`,
        { outputFormat }
      );

      return response.data;
    } catch (error: any) {
      console.error('Failed to regenerate report:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to regenerate report'
      };
    }
  }

  // =====================================================================
  // Report Sharing
  // =====================================================================

  /**
   * Create a shareable link for a report
   */
  async shareReport(reportId: string, options: CreateShareRequest = {}): Promise<ShareResponse> {
    try {
      const response = await axiosInstance.post<ShareResponse>(
        `${API_BASE_URL}/${reportId}/share`,
        options
      );

      return response.data;
    } catch (error: any) {
      console.error('Failed to create share link:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create share link'
      };
    }
  }

  /**
   * Get all shares created by the user
   */
  async getShares(): Promise<ReportShare[]> {
    try {
      const response = await axiosInstance.get<ShareListResponse>(`${API_BASE_URL}/shares`);

      return response.data.shares || [];
    } catch (error: any) {
      console.error('Failed to fetch shares:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch shares');
    }
  }

  /**
   * Revoke a share link
   */
  async revokeShare(shareId: string): Promise<void> {
    try {
      await axiosInstance.delete(`${API_BASE_URL}/shares/${shareId}`);
    } catch (error: any) {
      console.error('Failed to revoke share:', error);
      throw new Error(error.response?.data?.error || 'Failed to revoke share');
    }
  }

  /**
   * Download a shared report (public, no auth required)
   * This uses a direct fetch instead of axiosInstance to avoid auth headers
   */
  async downloadSharedReport(shareToken: string, password?: string): Promise<{
    success: boolean;
    blob?: Blob;
    filename?: string;
    error?: string;
    requires_password?: boolean;
  }> {
    try {
      const url = new URL(`${API_CONFIG.BASE_URL}${API_BASE_URL}/shared/${shareToken}`);
      if (password) {
        url.searchParams.set('password', password);
      }

      const response = await fetch(url.toString());

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          error: errorData.error || 'Failed to download shared report',
          requires_password: errorData.requires_password
        };
      }

      const blob = await response.blob();
      const filename = response.headers.get('content-disposition')
        ?.split('filename=')[1]
        ?.replace(/"/g, '') || 'shared-report.pdf';

      return {
        success: true,
        blob,
        filename
      };
    } catch (error: any) {
      console.error('Failed to download shared report:', error);
      return {
        success: false,
        error: error.message || 'Failed to download shared report'
      };
    }
  }

  // =====================================================================
  // Data Source Checking
  // =====================================================================

  /**
   * Check data availability for a hotel
   */
  async getDataSources(hotelId: string): Promise<DataSourceMap> {
    try {
      const response = await axiosInstance.get<DataSourcesResponse>(
        `${API_BASE_URL}/data-sources`,
        { params: { hotelId } }
      );

      return {
        hotel_id: response.data.hotel_id || hotelId,
        data_sources: response.data.data_sources || {}
      };
    } catch (error: any) {
      console.error('Failed to check data sources:', error);
      throw new Error(error.response?.data?.error || 'Failed to check data sources');
    }
  }
}

const reportService = new ReportService();
export default reportService;
